import { Controller } from "@hotwired/stimulus";
import { TelnyxRTC } from "@telnyx/webrtc";

export default class extends Controller {
  connect() {
    // Check if this is actually a Telnyx call
    const isTwilio = this.element.getAttribute("data-controller").includes("twilio-client");
    if (isTwilio) {
      console.warn("Telnyx: Skipping initialization on Twilio call.");
      return;
    }

    this.currentCall = window.currentTelnyxCall || null;
    this.deviceReady = false;
    this.isMuted = false;
    this.selectedSpeakerId = null;
    this.selectedMicId = null;

    if (window.telnyxDevice && window.telnyxDevice.state !== "disconnected") {
      this.device = window.telnyxDevice;
      this.deviceReady = this.telnyxDeviceReady(this.device);
      this.remoteAudio = document.getElementById("telnyx-remote-audio");
      this.selectAudioDevices().then(({ speakerId }) => this.setOutputDevice(speakerId));
      this.setupAudioUnlock();
      this.setupDeviceChangeListener();
      return;
    }

    // Ensure Twilio is fully disabled to prevent browser resource conflicts
    this.ensureTwilioDisabled();
    this.initializeDevice();
    this.setupAudioUnlock();
  }

  // Set up audio unlock for browser autoplay policy
  // User interaction is required before audio can play automatically
  setupAudioUnlock() {
    if (this.audioUnlockHandler) return;

    this.audioUnlockHandler = () => {
      this.resumeAudioContext();
    };

    document.addEventListener("click", this.audioUnlockHandler, { passive: true });
    document.addEventListener("keydown", this.audioUnlockHandler, { passive: true });
  }

  // Explicitly destroy Twilio device if it exists
  ensureTwilioDisabled() {
    if (window.twilioDevice) {
      console.warn("Telnyx: Disconnecting from Twilio to prevent conflicts...");
      try {
        if (window.currentTwilioCall) {
          window.currentTwilioCall.disconnect();
          window.currentTwilioCall = null;
        }
        window.twilioDevice.destroy();
        window.twilioDevice = null;
      } catch (e) {
        console.warn("Telnyx: Error destroying Twilio device:", e);
      }
    }
  }

  disconnect() {
    // Clean up audio unlock handler
    if (this.audioUnlockHandler) {
      document.removeEventListener("click", this.audioUnlockHandler);
      document.removeEventListener("keydown", this.audioUnlockHandler);
      this.audioUnlockHandler = null;
    }

    if (this.deviceChangeHandler) {
      navigator.mediaDevices.removeEventListener("devicechange", this.deviceChangeHandler);
      this.deviceChangeHandler = null;
    }
  }

  async initializeDevice() {
    try {
      const response = await fetch("/api/telnyx/credential_token", {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector("meta[name=\"csrf-token\"]").content,
          "Content-Type": "application/json"
        }
      });
      if (!response.ok) throw new Error("Failed to fetch Telnyx WebRTC token");
      const data = await response.json();
      const token = typeof data === "string" ? data : (data.data || data.token);
      const { speakerId } = await this.selectAudioDevices();

      this.device = new TelnyxRTC({
        login_token: token,
      });

      // Create audio element for remote stream (required to hear the other party)
      this.remoteAudio = document.getElementById("telnyx-remote-audio");
      if (!this.remoteAudio) {
        this.remoteAudio = document.createElement("audio");
        this.remoteAudio.id = "telnyx-remote-audio";
        this.remoteAudio.autoplay = true;
        // Required for iOS Safari
        this.remoteAudio.setAttribute("playsinline", "");
        document.body.appendChild(this.remoteAudio);
      }
      // Connect the remote stream to the audio element
      this.device.remoteElement = this.remoteAudio;
      await this.setOutputDevice(speakerId);

      this.device.on("telnyx.ready", () => {
        this.deviceReady = true;
        console.warn("Telnyx Client ready");
        // Resume audio context on ready (browser autoplay policy)
        this.resumeAudioContext();
        this.setOutputDevice(this.selectedSpeakerId);
      });

      this.device.on("telnyx.error", (error) => {
        this.deviceReady = false;
        console.error("Telnyx Client error:", error);
      });

      this.device.on("telnyx.notification", (notification) => {
        // Handle incoming calls or other notifications
        if (notification.type === "callUpdate") {
          const call = notification.call;
          if (call.state === "ringing" && call.direction === "inbound") {
            if (Notification.permission === "granted") {
              new Notification("Incoming Call", {
                body: `From: ${call.options.remoteCallerName || "Unknown"}`,
                icon: "/favicon.ico",
              });
            }
          }

          // Monitor outbound call state changes
          if (this.currentCall && this.currentCall.id === call.id) {
            if (call.state === "active") {
              // Answered!
              console.warn("Telnyx Call became active:", call.id);
              // Resume audio context when call becomes active (browser autoplay policy)
              this.resumeAudioContext();
              this.setOutputDevice(this.selectedSpeakerId, call);
              this.dispatchCallEvent("accepted", { conferenceName: this.currentConferenceName, isConference: true });
            } else if (call.state === "destroy") {
              // Hung up!
              console.warn("Telnyx Call destroyed:", call.id);
              this.currentCall = null;
              this.currentConferenceName = null;
              this.isMuted = false;
              window.currentTelnyxCall = null;
              this.dispatchCallEvent("disconnected", { conferenceName: this.currentConferenceName, isConference: true });
            }
          }
        }
      });

      this.device.connect();
      window.telnyxDevice = this.device;

      this.setupDeviceChangeListener();
    } catch (error) {
      console.error("Failed to initialize Telnyx Device:", error);
    }
  }

  isReady() {
    return this.deviceReady === true || this.telnyxDeviceReady(this.device);
  }

  telnyxDeviceReady(device) {
    return ["connected", "ready"].includes(device?.state);
  }

  setupDeviceChangeListener() {
    if (this.deviceChangeHandler) return;

    this.deviceChangeHandler = async () => {
      console.warn("Audio device change detected (Telnyx)");
      await this.handleDeviceChange();
    };

    navigator.mediaDevices.addEventListener("devicechange", this.deviceChangeHandler);
  }

  async selectAudioDevices() {
    let stream = null;

    try {
      if (!navigator.mediaDevices?.getUserMedia || !navigator.mediaDevices?.enumerateDevices) {
        return { micId: null, speakerId: null };
      }

      stream = await navigator.mediaDevices.getUserMedia({
        audio: this.audioConstraints()
      });

      const devices = await navigator.mediaDevices.enumerateDevices();
      const microphones = devices.filter(device => device.kind === "audioinput");
      const speakers = devices.filter(device => device.kind === "audiooutput");
      const mic = this.preferredDevice(microphones);
      const speaker = this.preferredDevice(speakers);

      this.selectedMicId = mic?.deviceId || null;
      this.selectedSpeakerId = speaker?.deviceId || null;

      console.warn("Telnyx audio devices detected:", {
        microphones: microphones.map(device => ({ id: device.deviceId, label: device.label })),
        speakers: speakers.map(device => ({ id: device.deviceId, label: device.label })),
        selectedMic: this.selectedMicId,
        selectedSpeaker: this.selectedSpeakerId
      });

      return {
        micId: this.selectedMicId,
        speakerId: this.selectedSpeakerId
      };
    } catch (error) {
      console.warn("Telnyx could not enumerate audio devices, falling back to browser defaults:", error);
      return { micId: null, speakerId: null };
    } finally {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    }
  }

  preferredDevice(devices) {
    return devices.find(device => device.deviceId === "default") || devices[0];
  }

  audioConstraints(deviceId = null) {
    const constraints = {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true
    };

    if (deviceId) {
      constraints.deviceId = { exact: deviceId };
    }

    return constraints;
  }

  async setOutputDevice(speakerId, call = this.currentCall || window.currentTelnyxCall) {
    if (!speakerId) return;

    try {
      if (this.remoteAudio && typeof this.remoteAudio.setSinkId === "function") {
        await this.remoteAudio.setSinkId(speakerId);
      }

      if (this.device) {
        this.device.speaker = speakerId;
      }

      if (call && typeof call.setAudioOutDevice === "function") {
        await call.setAudioOutDevice(speakerId);
      }

      this.selectedSpeakerId = speakerId;
      console.warn("Telnyx speaker device set to:", speakerId);
    } catch (error) {
      console.warn("Telnyx could not set speaker device:", error);
    }
  }

  async setInputDevice(micId, call = this.currentCall || window.currentTelnyxCall) {
    if (!micId) return;

    try {
      if (call && typeof call.setAudioInDevice === "function") {
        await call.setAudioInDevice(micId, this.isMuted);
      }

      this.selectedMicId = micId;
      console.warn("Telnyx microphone device set to:", micId);
    } catch (error) {
      console.warn("Telnyx could not switch microphone device:", error);
    }
  }

  async handleDeviceChange() {
    try {
      if (!navigator.mediaDevices?.enumerateDevices) return;

      const devices = await navigator.mediaDevices.enumerateDevices();
      const microphones = devices.filter(device => device.kind === "audioinput");
      const speakers = devices.filter(device => device.kind === "audiooutput");
      const mic = this.preferredDevice(microphones);
      const speaker = this.preferredDevice(speakers);

      console.warn("Telnyx device change - new defaults:", {
        mic: mic?.label,
        speaker: speaker?.label
      });

      if (speaker && speaker.deviceId !== this.selectedSpeakerId) {
        await this.setOutputDevice(speaker.deviceId);
      }

      if (mic && mic.deviceId !== this.selectedMicId) {
        await this.setInputDevice(mic.deviceId);
      }
    } catch (error) {
      console.warn("Telnyx error handling audio device change:", error);
    }
  }

  // Resume audio context (required for browser autoplay policy)
  resumeAudioContext() {
    try {
      // Try to resume the remote audio element
      if (this.remoteAudio && this.remoteAudio.paused) {
        this.remoteAudio.play().catch(e => {
          console.warn("Could not auto-play remote audio:", e);
        });
      }

      // Also try the device's audio context if available
      const audioContext = this.device?.audioContext ||
                          this.device?._audioContext ||
                          this.device?.webRTCClient?.audioContext;

      if (audioContext && audioContext.state === "suspended") {
        audioContext.resume().then(() => {
          console.warn("Telnyx audio context resumed");
        }).catch(e => {
          console.warn("Could not resume audio context:", e);
        });
      }
    } catch (error) {
      console.warn("Unable to resume Telnyx audio context:", error);
    }
  }

  dispatchCallEvent(eventName, detail) {
    const event = new CustomEvent(`telnyx:call:${eventName}`, {
      detail,
      bubbles: true,
      cancelable: true,
    });
    this.element.dispatchEvent(event);
  }

  async joinConference(conferenceName) {
    if (!this.device) {
      console.error("Telnyx Device not initialized");
      return null;
    }

    if (!this.isReady()) {
      console.warn("Telnyx Device is not ready yet; deferring conference join", { state: this.device?.state });
      return null;
    }

    if (!conferenceName) {
      console.error("Conference name is required");
      return null;
    }

    if (this.currentCall || window.currentTelnyxCall) {
      console.warn("A call is already active");
      this.dispatchCallEvent("error", {
        conferenceName,
        error: { message: "A call is already active. Please end the current call first." },
        isConference: true
      });
      return null;
    }

    try {
      // Telnyx SIP proxy requires a valid E.164 destinationNumber (or a valid SIP URI).
      // If we pass "call_244" as destinationNumber, it rejects the call immediately.
      // We dial our own Telnyx app number, and pass the conference name in `clientState`.
      // The Webhook API checks `clientState` to know this is an agent joining `call_243`.
      const telnyxNumber = this.element.getAttribute("data-active-call-telnyx-number-value") || "+12069900049";

      const call = this.device.newCall({
        destinationNumber: telnyxNumber,
        clientState: conferenceName,
        audio: this.audioConstraints(this.selectedMicId),
        micId: this.selectedMicId,
        speakerId: this.selectedSpeakerId,
        remoteElement: this.remoteAudio,
        video: false
      });

      this.currentCall = call;
      this.currentConferenceName = conferenceName;
      this.isMuted = false;
      window.currentTelnyxCall = call;
      await this.setOutputDevice(this.selectedSpeakerId, call);

      // Removed call.on() because @telnyx/webrtc v2 Call objects do not extend EventEmitter.
      // Call state changes (active, destroy) are handled in the "telnyx.notification" device listener.

      return call;
    } catch (error) {
      console.error("Error joining conference via Telnyx:", error);
      this.dispatchCallEvent("error", { conferenceName, error, isConference: true });
      return null;
    }
  }

  hangUp() {
    if (this.currentCall) {
      try {
        this.currentCall.hangup();
        this.currentCall = null;
        this.isMuted = false;
        window.currentTelnyxCall = null;
      } catch (error) {
        console.error("Error hanging up Telnyx call:", error);
      }
    } else if (window.currentTelnyxCall) {
      try {
        window.currentTelnyxCall.hangup();
        window.currentTelnyxCall = null;
        this.currentCall = null;
        this.isMuted = false;
      } catch (error) {
        console.error("Error hanging up Telnyx call (fallback):", error);
      }
    }
  }

  toggleMute() {
    if (this.currentCall) {
      try {
        if (this.isMuted) {
          this.currentCall.unmuteAudio();
        } else {
          this.currentCall.muteAudio();
        }
        this.isMuted = !this.isMuted;
        return this.isMuted;
      } catch (error) {
        console.error("Error toggling mute Telnyx:", error);
        return false;
      }
    }
    return false;
  }

  sendDigit(digit) {
    const call = this.currentCall || window.currentTelnyxCall;

    if (!call || typeof call.dtmf !== "function") {
      console.warn("Telnyx: Cannot send DTMF without an active call");
      return false;
    }

    try {
      call.dtmf(digit);
      return true;
    } catch (error) {
      console.error("Telnyx: Failed to send DTMF", error);
      return false;
    }
  }
}
